// public/js/modules/map/tether-overlay.js
// Draws a dashed connector between a tethered beacon and its home gateway when
// the two have separated (the beacon's asset position drifted away from the
// gateway's asset position). While together they sit at ~the same coordinates,
// so no line is drawn and their two pins naturally overlap as "one".
//
// Fully self-contained + defensive: any failure is swallowed so it can never
// break the base map render.
import { isValidLatLon } from './constants.js';

const SRC = 'tether-lines';
const LINE_LAYER = 'tether-line';
const LABEL_LAYER = 'tether-line-label';

// Distance (metres) beyond which a tethered pair counts as "separated".
const SEPARATION_M = 50;

function metres(aLat, aLon, bLat, bLon) {
  const R = 6371000, toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(bLat - aLat), dLon = toRad(bLon - aLon);
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// Find the asset that currently carries a given gateway deviceId.
function assetForDevice(assets, deviceId) {
  if (!deviceId) return null;
  return assets.find(a => (a.assignedDeviceIds || []).includes(deviceId)) || null;
}

function buildFeatures(assets) {
  const features = [];
  for (const asset of assets || []) {
    for (const slot of (asset.deviceSlots || [])) {
      if (!slot.tethered || !slot.deviceId || !slot.homeGatewayId) continue; // cross-asset tether only
      const home = assetForDevice(assets, slot.homeGatewayId);
      if (!home || home.assetUUID === asset.assetUUID) continue;
      const aLat = parseFloat(asset.lastLat), aLon = parseFloat(asset.lastLon);
      const hLat = parseFloat(home.lastLat), hLon = parseFloat(home.lastLon);
      if (!isValidLatLon(aLat, aLon) || !isValidLatLon(hLat, hLon)) continue;
      const dist = metres(aLat, aLon, hLat, hLon);
      if (dist <= SEPARATION_M) continue; // together → no connector
      features.push({
        type: 'Feature',
        properties: { label: `${Math.round(dist)} m` },
        geometry: { type: 'LineString', coordinates: [[aLon, aLat], [hLon, hLat]] },
      });
    }
  }
  return features;
}

export function renderTetherOverlay(assets, map) {
  try {
    if (!map || typeof map.addSource !== 'function') return;
    const data = { type: 'FeatureCollection', features: buildFeatures(assets) };
    const src = map.getSource(SRC);
    if (src) { src.setData(data); return; }
    map.addSource(SRC, { type: 'geojson', data });
    map.addLayer({
      id: LINE_LAYER, type: 'line', source: SRC,
      layout: { 'line-cap': 'round' },
      paint: { 'line-color': '#f59e0b', 'line-width': 2, 'line-dasharray': [2, 2] },
    });
    map.addLayer({
      id: LABEL_LAYER, type: 'symbol', source: SRC,
      layout: {
        'symbol-placement': 'line-center', 'text-field': ['get', 'label'],
        'text-size': 11, 'text-offset': [0, -0.8],
      },
      paint: { 'text-color': '#b45309', 'text-halo-color': '#fff', 'text-halo-width': 1.5 },
    });
  } catch (e) {
    console.warn('[tether] overlay failed:', e?.message);
  }
}
